import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import {
  ReactiveBase,
  ReactiveList,
  ReactiveComponent,
  DataSearch,
  SelectedFilters,
  MultiDataList,
} from '@appbaseio/reactivesearch';

import { Slideout } from '../../components/ui/view/Slideout';
import { FairInteractiveMap as FairInteractiveMapComponent } from '../../components/public/FairInteractiveMap';
import { FairInteractiveMapFilterBar } from '../../components/public/FairInteractiveMapFilterBar';
import { FairInteractiveMapFloorSelector } from '../../components/public/FairInteractiveMapFloorSelector';
import { FairInteractiveMapDetails } from '../../components/public/FairInteractiveMapDetails';

function FairInteractiveMap({
  exhibitorsUrl,
  exhibitorsCredentials,
  exhibitorsApp,
  artworksUrl,
  artworksCredentials,
  artworksApp,
  fairTitle,
  showArtworks,
  pois,
  mapBgColor,
  hideHome,
  floors,
  children,
}) {
  const hasFiltersSelected = useRef(false);
  const qSelected = useRef(null);
  const lastFilterInteraction = useRef(null);
  const setFilterValue = useRef(null);
  const clearFilterValues = useRef(null);
  const allStandsInFocus = useRef([]);

  const [stands, setStands] = useState(null);
  const [sections, setSections] = useState(null);
  const [artworks, setArtworks] = useState({});
  const [standsInFocus, setStandsInFocus] = useState(null);
  const [standInFocusFromQuery, setStandInFocusFromQuery] = useState(null);
  const [selectedStand, setSelectedStand] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);

  const handleOnStandClick = useCallback((standNumber) => {
    setSelectedStand(standNumber);
    const exhibitor = stands[standNumber] || null;
    if (setFilterValue.current && qSelected.current && exhibitor) {
      setFilterValue.current('q', exhibitor.title);
    }
  }, [stands]);

  const handleOnOutsideClick = useCallback(() => {
    setSelectedStand((oldStand) => {
      if (oldStand && setFilterValue.current) {
        setTimeout(() => setFilterValue.current('q', null), 0);
      }
      return null;
    });
  }, []);

  const handleOnExhibitorClose = useCallback(() => {
    setSelectedStand(null);
    if (setFilterValue.current) {
      setFilterValue.current('q', null);
    }
  }, []);

  const handleFilterChange = useCallback(() => {
    const now = Date.now();
    if (!lastFilterInteraction.current
      || ((now - lastFilterInteraction.current) > 100)
    ) {
      lastFilterInteraction.current = now;
      if (setFilterValue.current) {
        setFilterValue.current('q', null);
        setSelectedFloor(null);
      }
    }
  }, []);

  const handleQueryChange = useCallback((q) => {
    const selectedStandNumberFromQuery = q ? Number(q) : null;
    const selectedStandFromQuery = selectedStandNumberFromQuery && stands
      ? (stands[selectedStandNumberFromQuery] || null)
      : null;
    setStandInFocusFromQuery(selectedStandFromQuery
      ? selectedStandNumberFromQuery
      : null);

    const now = Date.now();
    if (!lastFilterInteraction.current
      || ((now - lastFilterInteraction.current) > 100)
    ) {
      lastFilterInteraction.current = now;
      if (setFilterValue.current) {
        setFilterValue.current('fairSections', []);
        setSelectedFloor(null);
      }
    }
  }, [stands]);

  const handleFloorChange = useCallback((floor) => {
    setSelectedFloor(floor);
    const now = Date.now();
    if (!lastFilterInteraction.current
      || ((now - lastFilterInteraction.current) > 100)
    ) {
      lastFilterInteraction.current = now;
      if (clearFilterValues.current) {
        clearFilterValues.current();
      }
    }
  }, []);

  const isLoaded = stands !== null;
  const isFullyLoaded = standsInFocus !== null;
  const selectedExhibitor = isLoaded && selectedStand ? (stands[selectedStand] || null) : null;

  const floorFilter = ((floors || [])[selectedFloor] || []).map((standNumber) => (`${standNumber}`));
  allStandsInFocus.current = isFullyLoaded
    ? [
      ...((hasFiltersSelected.current || !!qSelected.current) ? [] : (pois || [])),
      ...(standsInFocus || []),
      standInFocusFromQuery,
      (selectedExhibitor ? selectedStand : null),
    ].filter((standNumber) => {
      if (!standNumber) {
        return false;
      }
      if (floorFilter.length === 0) {
        return true;
      }
      return floorFilter.includes(`${standNumber}`);
    })
    : [];

  return (
    <>
      <ReactiveBase
        url={exhibitorsUrl}
        credentials={exhibitorsCredentials}
        app={exhibitorsApp}
        enableAppbase
      >
        <ReactiveList
          componentId="exhibitors"
          dataField="id"
          react={isLoaded
            ? { and: ['fairTitle'], or: ['fairSections', 'q'] }
            : { and: ['fairTitle'] }}
          size={1000}
          scrollOnChange={false}
          pagination={false}
          infiniteScroll={false}
          showLoader={false}
          showResultStats={false}
          showExport={false}
          loader={() => null}
          renderError={() => null}
          renderResultStats={() => null}
          renderNoResults={() => null}
          renderPagination={() => null}
          renderExport={() => null}
          render={() => null}
          onData={({ data }) => {
            const newStands = {};
            const newSections = {};
            data.forEach((exhibitor) => {
              const participation = (exhibitor.participations || [])
                .find((p) => p.fair?.title === fairTitle);
              if (participation) {
                const fairSections = participation.fairSections || null;
                const standNumber = participation.standNumber || null;
                const image = exhibitor.locations && exhibitor.locations.length > 0
                  ? (exhibitor.locations[0].image?.transforms.catalog.url || null)
                  : null;
                const newExhibitor = {
                  id: exhibitor.id,
                  title: exhibitor.title,
                  url: exhibitor.url,
                  fairSections,
                  standNumber,
                  image,
                };
                if (standNumber) {
                  newStands[standNumber] = newExhibitor;
                }
                if (fairSections) {
                  newSections[fairSections] = [...(newSections[fairSections] || []), newExhibitor];
                }
              }
            });
            if (!isLoaded) {
              setStands(newStands);
              setSections(newSections);
            }
            if (isLoaded || !hasFiltersSelected.current) {
              setStandsInFocus(Object.keys(newStands));

              const selectedStandNumberFromQuery = qSelected.current
                ? Number(qSelected.current)
                : null;
              const selectedStandFromQuery = selectedStandNumberFromQuery && stands
                ? (stands[selectedStandNumberFromQuery] || null)
                : null;
              setStandInFocusFromQuery(selectedStandFromQuery
                ? selectedStandNumberFromQuery
                : null);
            }
          }}
        />
        <ReactiveComponent
          componentId="fairTitle"
          customQuery={() => ({
            query: {
              bool: {
                must: [
                  {
                    match: {
                      'participations.fair.title.keyword': fairTitle,
                    },
                  },
                ],
              },
            },
          })}
        />
        <SelectedFilters render={({ selectedValues, setValue, clearValues }) => {
          const selectedCategories = selectedValues.fairSections?.value || [];
          const selectedSearchTerm = selectedValues.q?.value || null;

          setFilterValue.current = setValue;
          clearFilterValues.current = clearValues;
          hasFiltersSelected.current = selectedCategories.length > 0 || !!selectedSearchTerm;
          qSelected.current = selectedSearchTerm;

          const fairSections = Object.keys(sections || {});

          return (
            <FairInteractiveMapFilterBar
              numberOfResults={allStandsInFocus.current.length}
              selectedCategories={selectedCategories}
              setValue={setValue}
              clearValues={clearValues}
              renderFilterList={fairSections.length > 0 ? (
                <MultiDataList
                  URLParams
                  componentId="fairSections"
                  dataField="participations.fairSections.keyword"
                  react={{ and: ['fairTitle'] }}
                  data={fairSections
                    .sort()
                    .map((section) => ({
                      label: section,
                      value: section,
                    }))}
                  showCheckbox
                  showSearch={false}
                  onValueChange={handleFilterChange}
                />
              ) : null}
              renderFloorSelector={floors ? (
                <FairInteractiveMapFloorSelector
                  data={Object.keys(floors).filter(Boolean)}
                  value={selectedFloor}
                  onChange={handleFloorChange}
                />
              ) : null}
              renderSearchBox={(
                <DataSearch
                  URLParams
                  componentId="q"
                  dataField={['title']}
                  react={{ and: ['fairTitle'] }}
                  queryFormat="and"
                  fuzziness="AUTO"
                  filterLabel="Search term"
                  placeholder="Search for exhibitors"
                  iconPosition="right"
                  showClear
                  onValueSelected={handleQueryChange}
                />
              )}
            />
          );
        }}
        />
        {isFullyLoaded && (
          <FairInteractiveMapComponent
            stands={Object.keys(stands)}
            pois={pois}
            standsInFocus={allStandsInFocus.current}
            bgColor={mapBgColor || '#292929'}
            disableBgColor="#505050"
            notInFocusOpacity={0.4}
            withoutHome={hideHome}
            onStandClick={handleOnStandClick}
            onOutsideClick={handleOnOutsideClick}
          >
            {children}
          </FairInteractiveMapComponent>
        )}
      </ReactiveBase>
      {showArtworks && selectedExhibitor && (
        <ReactiveBase
          url={artworksUrl}
          credentials={artworksCredentials}
          app={artworksApp}
          enableAppbase
        >
          <ReactiveList
            componentId="artworks"
            dataField="id"
            react={{ and: ['fairTitle', 'exhibitorId'] }}
            size={1000}
            scrollOnChange={false}
            pagination={false}
            infiniteScroll={false}
            showLoader={false}
            showResultStats={false}
            showExport={false}
            loader={() => null}
            renderError={() => null}
            renderResultStats={() => null}
            renderNoResults={() => null}
            renderPagination={() => null}
            renderExport={() => null}
            render={() => null}
            onData={({ data }) => {
              const newArtworks = {};
              data.forEach((artwork) => {
                const { id } = artwork.account;
                newArtworks[id] = [...(newArtworks[id] || []), {
                  id: artwork.id,
                  url: artwork.url,
                  artist: [artwork.attribution, artwork.primaryArtistName]
                    .filter(Boolean)
                    .join(' ')
                    .trim(),
                  title: artwork.title,
                  year: artwork.year,
                  image: artwork.image?.transforms?.thumbnail?.url || null,
                }];
              });
              setArtworks((currentArtworks) => ({
                ...currentArtworks,
                ...newArtworks,
              }));
            }}
          />
          <ReactiveComponent
            componentId="fairTitle"
            customQuery={() => ({
              query: {
                bool: {
                  must: [
                    {
                      match: {
                        'fair.title.keyword': fairTitle,
                      },
                    },
                  ],
                },
              },
            })}
          />
          <ReactiveComponent
            componentId="exhibitorId"
            customQuery={() => ({
              query: {
                bool: {
                  must: [
                    {
                      match: {
                        'account.id': selectedExhibitor.id,
                      },
                    },
                  ],
                },
              },
            })}
          />
        </ReactiveBase>
      )}
      {createPortal(
        <Slideout
          isOpen={!!selectedExhibitor}
          onClose={handleOnExhibitorClose}
          headingLabel="Exhibitor"
          // eslint-disable-next-line react/jsx-no-useless-fragment
          headingIcon={(<></>)}
          hideButtons
          backdropBehavior="click-through"
        >
          <FairInteractiveMapDetails
            exhibitor={selectedExhibitor}
            artworks={artworks[selectedExhibitor?.id || 0] || []}
          />
        </Slideout>,
        document.body,
      )}
    </>
  );
}

export { FairInteractiveMap };

FairInteractiveMap.propTypes = {
  exhibitorsUrl: PropTypes.string.isRequired,
  exhibitorsCredentials: PropTypes.string.isRequired,
  exhibitorsApp: PropTypes.string.isRequired,
  artworksUrl: PropTypes.string.isRequired,
  artworksCredentials: PropTypes.string.isRequired,
  artworksApp: PropTypes.string.isRequired,
  fairTitle: PropTypes.string.isRequired,
  showArtworks: PropTypes.bool,
  pois: PropTypes.arrayOf(PropTypes.string),
  mapBgColor: PropTypes.string,
  hideHome: PropTypes.bool,
  floors: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ),
  children: PropTypes.shape({ id: PropTypes.string }),
};

FairInteractiveMap.defaultProps = {
  showArtworks: false,
  pois: [],
  mapBgColor: null,
  hideHome: false,
  floors: null,
  children: null,
};
